<template>
  <div class="color-swatch">
    <a
      v-if="isProductCard"
      class="color-swatches__swatch"
      :class="{ [$class('active')]: isActiveSwatch }"
      :href="getHref"
      :style="swatchUrl"
      @focus="handleSwatchFocus"
      @mouseover="handleSwatchFocus"
    >
      <span
        class="visually-hidden"
        v-text="colorLabel"
      />
    </a>

    <template v-else>
      <input
        :id="`${namespace}-${productHandle}-color-swatch-${colorCode}`"
        v-model="activeColor"
        class="selection-tab__input visually-hidden"
        :class="{ 'js-line-item-option': isLineItem }"
        :checked="isActiveSwatch"
        :name="inputName"
        :disabled="disableInputs"
        type="radio"
        :value="colorLabel"
        @change="handleSwatchFocus"
        @keydown.enter.prevent="updateActiveSwatch"
      >

      <label
        class="
        color-swatches__swatch
        selection-tab__label
        text-body-s-regular
        text-body-m-regular-desktop
      "
        :class="{ [$class('active')]: isActiveSwatch }"
        :for="`${namespace}-${productHandle}-color-swatch-${colorCode}`"
        :style="swatchUrl"
      >
        <span
          class="visually-hidden"
          v-text="colorLabel"
        />
      </label>
    </template>
  </div>
</template>

<script>

/**
 * Vue: Color swatch (color-swatch)
 * -----------------------------------------------------------------------------
 * Snippet for exact color swatch element.
 *
 * @param {String} activeProductHandle - Product handle for active product.
 * @param {String} colorCode - 3 character color code.
 * @param {String} colorLabel - Color label without the 3 char code.
 * @param {String} colorValue - Color value.
 * @param {String} featuredImageSrc - Featured image source URL.
 * @param {String} [inputName] - Color swatch input name.
 * @param {Boolean} [isProductCard] - If component is used in product card.
 * @param {Boolean} [isLineItem] - If component is used in line item.
 * @param {String} namespace - Color swatch namespace.
 * @param {String} productHandle - Product handle for this swatch.
 * @param {Array} variants - Product variants array.
 * @param {Array} isActive - If dropdown in cart is active.
 *
 * @emits updateActiveSwatch - Emit active color code.
 *
 */
import { isColorCodeInStr } from '~/helpers/product'

export default {
  name: 'ColorSwatch',

  props: {
    activeProductHandle: {
      type: String,
      default: '',
    },
    colorCode: {
      type: String,
      default: '',
      required: true,
    },
    colorLabel: {
      type: String,
      default: '',
      required: true,
    },
    colorValue: {
      type: String,
      default: '',
      required: true,
    },
    featuredImageSrc: {
      type: String,
      default: '',
      required: true,
    },
    inputName: {
      type: String,
      default: '',
    },
    isProductCard: {
      type: Boolean,
      default: false,
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    productHandle: {
      type: String,
      default: '',
      required: true,
    },
    setInitialSwatch: {
      type: Boolean,
      default: true,
    },
    isLineItem: {
      type: Boolean,
      default: false,
    },
    variants: {
      type: [Boolean, Array],
      default: false,
      required: true,
    },
    isActive: {
      type: Boolean,
      default: false,
      required: false,
    },
  },

  emits: {
    'update-active-swatch': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      activeColor: false,
    }
  },

  computed: {

    /**
     * Return the variant URL that matches the selected color swatch.
     * @returns {String}
     */
    getHref() {
      const matchingVariant = this.getMatchingVariant()

      return `https://${location.host}${cnvs.routes.normalisedRoot}products/${this.productHandle}?variant=${matchingVariant.id}`
    },

    /**
     * Return if the current swatch is the active swatch.
     * @returns {Boolean}
     */
    isActiveSwatch() {
      const isInString = isColorCodeInStr(this.colorCode, this.featuredImageSrc)

      return this.activeProductHandle && this.productHandle
        ? isInString && this.activeProductHandle === this.productHandle
        : isInString
    },

    /**
     * Search through product variants for the color swatch's matching swatch
     * image URL.
     * @returns {String}
     */
    swatchUrl() {
      return `background-image: url('${this.getMatchingVariant().swatchUrl}')`
    },

    /**
     * Disable inputs when dropdown is inactive for line items.
     */
    disableInputs() {
      return this.isLineItem && !this.isActive
    },
  },

  watch: {

    /**
     * Watch isActiveSwatch to ensure that the emitter for the code/label/value
     * runs after the value has been computed.
     */
    isActiveSwatch() {
      if (!this.isActiveSwatch) {
        return
      }

      this.updateActiveSwatch()
    },
  },

  mounted() {
    this.setInitialActiveColor()
  },

  methods: {

    /**
     * Return the variant object that includes the specified color value.
     * @returns {Object}
     */
    getMatchingVariant() {
      return this.variants.find((variant) => {
        return isColorCodeInStr(this.colorCode, variant.swatchUrl)
      })
    },

    /**
     * Handle swatch focus.
     * For product cards, update active swatch.
     * For line items, do nothing.
     */
    handleSwatchFocus() {
      if (this.isLineItem) {
        return
      }

      this.updateActiveSwatch()
    },

    /**
     * Update active swatch.
     * @param {Boolean} initialSwatch - Whether this is the initial swatch-set.
     */
    updateActiveSwatch(initialSwatch = false) {
      this.$emit('update-active-swatch', {
        code: this.colorCode,
        label: this.colorLabel,
        value: this.colorValue,
        handle: this.productHandle,
        initialSwatch,
      })
    },

    /**
     * Emit the first active swatch.
     */
    setInitialActiveColor() {
      if (!this.isActiveSwatch || !this.setInitialSwatch) {
        return
      }

      this.updateActiveSwatch(true)
    },
  },
}
</script>
